import { Box, Card, Skeleton } from '@factorialco/deprecated-design-system'
import { AreaChartWidget } from '@factorialco/factorial-one-react/dist/experimental'
import { EmployeesInsightsDocument } from 'generated/resources/graphql'
import { last } from 'lodash'
import moment from 'moment'

import { observer } from 'mobx-react'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import * as styles from './index.vanilla.css'

interface Props {
  companyId: number
  endDate: string
  startDate: string
}

const chartDataTransformer = (categories?: string[], series?: number[]) => {
  if (!categories || !series) return []

  return categories.map((category, index) => {
    return {
      label: category,
      values: {
        value: series[index] || 0,
      },
    }
  })
}

const dateRangeParser = (dateString = '') => {
  return dateString ? moment(dateString, 'DD-MM-YYYY') : moment()
}

const dateRangeFormatter = (dateRange?: string) => {
  return dateRangeParser(dateRange)?.format('MMM')
}

const chartSummaryLabelFormatter = (dateRange?: string) => {
  if (!dateRange) return ''
  return dateRangeParser(dateRange)?.format('MMMM YYYY')
}

const CardSkeleton = () => {
  return (
    <Box flexGrow height="s140" width="full">
      <Card>
        <Card.Content>
          <Box height="s140" gap="s16" width="full">
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </Box>
        </Card.Content>
      </Card>
    </Box>
  )
}

const EmployeesInsights = ({ companyId, endDate, startDate }: Props) => {
  const { data, loading } = useQuery(EmployeesInsightsDocument, {
    variables: {
      companyId,
      endDate,
      startDate,
    },
  })

  return (
    <Box className={styles.content} gap="s16" flexDirection="row" width="full">
      <Box height="full" width="full" flexGrow>
        {!loading ? (
          <AreaChartWidget
            header={{
              title: i18n.t('employees.insights.headcount'),
              subtitle: chartSummaryLabelFormatter(
                last(data?.contracts.headcountCategoricalsConnection.nodes[0]?.categories)
              ),
            }}
            summaries={[
              {
                value: last(data?.contracts.headcountCategoricalsConnection.nodes[0]?.series) || 0,
                label: '',
              },
            ]}
            chart={{
              dataConfig: {
                value: {
                  color: '#0CA0AB',
                  label: i18n.t('employees.insights.headcount').toLowerCase(),
                },
              },
              yAxis: {
                hide: true,
              },
              data: chartDataTransformer(
                data?.contracts.headcountCategoricalsConnection.nodes[0]?.categories,
                data?.contracts.headcountCategoricalsConnection.nodes[0]?.series
              ),
              xAxis: {
                tickFormatter: dateRangeFormatter,
              },
            }}
          />
        ) : (
          <CardSkeleton />
        )}
      </Box>

      <Box height="full" width="full" flexGrow>
        {!loading ? (
          <AreaChartWidget
            header={{
              title: i18n.t('employees.insights.promotion_rate'),
              subtitle: chartSummaryLabelFormatter(
                last(data?.contracts.promotionsByMonthCategoricalsConnection.nodes[0]?.categories)
              ),
            }}
            summaries={[
              {
                value:
                  last(data?.contracts.promotionsByMonthCategoricalsConnection.nodes[0]?.series) ||
                  0,
                prefixUnit: '%',
                label: '',
              },
            ]}
            chart={{
              dataConfig: {
                value: {
                  color: '#927EF1',
                  label: i18n.t('employees.insights.promotion_rate').toLowerCase(),
                },
              },
              yAxis: {
                hide: true,
              },
              data: chartDataTransformer(
                data?.contracts.promotionsByMonthCategoricalsConnection.nodes[0]?.categories,
                data?.contracts.promotionsByMonthCategoricalsConnection.nodes[0]?.series
              ),
              xAxis: {
                tickFormatter: dateRangeFormatter,
              },
            }}
          />
        ) : (
          <CardSkeleton />
        )}
      </Box>

      <Box height="full" width="full" flexGrow>
        {!loading ? (
          <AreaChartWidget
            header={{
              title: i18n.t('employees.insights.turnover_rate'),
              subtitle: chartSummaryLabelFormatter(
                last(data?.contracts.turnoverCategoricalsConnection.nodes[0]?.categories)
              ),
            }}
            summaries={[
              {
                value: last(data?.contracts.turnoverCategoricalsConnection.nodes[0]?.series) || 0,
                prefixUnit: '%',
                label: '',
              },
            ]}
            chart={{
              dataConfig: {
                value: {
                  color: '#C85C80',
                  label: i18n.t('employees.insights.turnover_rate').toLowerCase(),
                },
              },
              data: chartDataTransformer(
                data?.contracts.turnoverCategoricalsConnection.nodes[0]?.categories,
                data?.contracts.turnoverCategoricalsConnection.nodes[0]?.series
              ),
              xAxis: {
                tickFormatter: dateRangeFormatter,
              },
              yAxis: {
                hide: true,
              },
            }}
          />
        ) : (
          <CardSkeleton />
        )}
      </Box>
    </Box>
  )
}

export default observer(EmployeesInsights)
